import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import CatalogueItemDialog from "components/CatalogueItemDialog";
import { addCatalogueItemData, updateCatalogueItemData } from "utils/api";

const toAccountOptions = (categories) =>
  categories.map((category) => ({
    label:
      category.categoryCode && category.categoryName
        ? `${category.categoryCode} - ${category.categoryName}`
        : category.categoryCodeName || category.categoryName,
    value: category.categoryCodeName || category.categoryName,
  }));

export default function AddItemDialog({
  accountOptions: suppliedAccountOptions = [],
  businessUserID,
  categories = [],
  darkMode,
  gstParameters = [],
  item,
  onClose,
  onSaved,
  readOnly = false,
  source,
  sourceID,
}) {
  const dispatch = useDispatch();
  const accountOptions = useMemo(
    () =>
      suppliedAccountOptions.length
        ? suppliedAccountOptions
        : toAccountOptions(categories),
    [categories, suppliedAccountOptions]
  );
  const [values, setValues] = useState(() => {
    const currentGst = gstParameters.find(
      (parameter) => String(parameter._id) === String(item?.gst)
    );
    const resolvedGst = Number(
      currentGst?.customValue1 ?? item?.gstRate ?? item?.gst
    );
    return {
      itemCode: item?.itemCode || "",
      itemName: item?.itemName || "",
      categoryCodeName:
        item?.categoryCodeName ||
        item?.categoryName ||
        accountOptions[0]?.value ||
        "",
      itemDescription: item?.itemDescription || "",
      status: item?.itemStatus || "Active",
      unit: item?.unitOfMeasurement || "each",
      itemPrice: item?.itemPrice ?? "",
      defaultQuantity: item?.itemQuantity ?? 1,
      gst: Number.isFinite(resolvedGst) ? resolvedGst : 10,
      discountType: item?.discountType || "None",
      discountValue: item?.discountValue || "",
    };
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);

  const update = (name, value) => {
    setValues((current) => ({
      ...current,
      [name]: value,
      ...(name === "discountType" && value === "None"
        ? { discountValue: "" }
        : {}),
    }));
    setFieldErrors((current) => ({ ...current, [name]: "" }));
    setSaveError("");
  };

  const validate = () => {
    const nextErrors = {};
    if (!values.itemCode.trim()) nextErrors.itemCode = "Enter an item code.";
    if (!values.itemName.trim()) nextErrors.itemName = "Enter an item name.";
    if (values.itemPrice === "") nextErrors.itemPrice = "Enter a unit price.";
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submit = async () => {
    if (saving || !validate()) return;
    const account = categories.find(
      (category) =>
        (category.categoryCodeName || category.categoryName) ===
        values.categoryCodeName
    ) ||
      accountOptions.find((option) => option.value === values.categoryCodeName);
    if (!account) {
      setSaveError("No active account is available for this item.");
      return;
    }

    setSaving(true);
    const saveItem = item ? updateCatalogueItemData : addCatalogueItemData;
    await saveItem(
      {
        ...(item?._id ? { _id: item._id } : {}),
        ...(item && source && sourceID ? { source, sourceID } : {}),
        businessUserID,
        itemCode: values.itemCode.trim(),
        itemName: values.itemName.trim(),
        itemDescription: values.itemDescription.trim(),
        itemStatus: values.status,
        unit: values.unit,
        itemPrice: Number(values.itemPrice),
        itemQuantity: Number(values.defaultQuantity) || 1,
        gst: Number(values.gst),
        discountType: values.discountType,
        discountValue: Number(values.discountValue) || 0,
        categoryName: account.categoryName || account.label || account.value,
        categoryCodeName: account.categoryCodeName || account.value,
      },
      (data) => {
        if (data?.Result_Status && data.Result_Output) {
          const output = Array.isArray(data.Result_Output)
            ? data.Result_Output[0]
            : data.Result_Output;
          onSaved({
            ...item,
            ...output,
            itemCode: values.itemCode.trim(),
            itemName: values.itemName.trim(),
            itemDescription: values.itemDescription.trim(),
            itemStatus: values.status,
            unit: values.unit,
            unitOfMeasurement: values.unit,
            itemPrice: Number(values.itemPrice),
            itemQuantity: Number(values.defaultQuantity) || 1,
            gst: Number(values.gst),
            discountType: values.discountType,
            discountValue: Number(values.discountValue) || 0,
            categoryName:
              account.categoryName || account.label || account.value,
            categoryCodeName: account.categoryCodeName || account.value,
          });
          return;
        }
        const message = data?.Result_Message || "We couldn't save this item.";
        if (/code already exist/i.test(message)) {
          setFieldErrors((current) => ({
            ...current,
            itemCode: "This item code already exists.",
          }));
        } else if (/name already exist/i.test(message)) {
          setFieldErrors((current) => ({
            ...current,
            itemName: "This item name already exists.",
          }));
        } else {
          setSaveError(message);
        }
        setSaving(false);
      },
      dispatch,
      { showLoader: false }
    );
  };

  return (
    <CatalogueItemDialog
      accountOptions={accountOptions}
      darkMode={darkMode}
      dialogLabel={item ? "Edit catalogue item" : "Add new catalogue item"}
      error={saveError}
      fieldErrors={fieldErrors}
      isSaving={saving}
      onChange={update}
      onClose={onClose}
      onSubmit={submit}
      submitDisabled={readOnly}
      submitLabel={item ? "Update item" : "Save item"}
      title={item ? "Edit item" : "Add new item"}
      values={values}
    />
  );
}
